import { useCallback, useEffect, useRef, useState } from "react";

export type AsyncState<T> = {
  data: T | undefined;
  error: Error | undefined;
  loading: boolean;
  /** Re-runs the loader, e.g. after a mutation. */
  reload: () => void;
};

/**
 * Runs an async loader and tracks its state.
 *
 * A small hand-rolled hook rather than a data-fetching library: this app makes a
 * handful of read calls per screen with no cross-screen cache to invalidate, and
 * pulling in a query client would be more machinery than the problem has.
 *
 * The result of a superseded call is discarded — without that, a slow response
 * for article A can land after a fast one for article B and render the wrong page.
 */
export function useAsync<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  deps: unknown[],
): AsyncState<T> {
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  // Identifies the newest run, so older ones can tell they have been superseded.
  const runId = useRef(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    const id = ++runId.current;
    const controller = new AbortController();

    setLoading(true);
    setError(undefined);

    loader(controller.signal)
      .then((result) => {
        if (id !== runId.current) return;
        setData(result);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (id !== runId.current || controller.signal.aborted) return;
        setError(err);
        setLoading(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { data, error, loading, reload };
}
